import Link from 'next/link'
import MainLayout from '../MainLayout/MainLayout'
import BidAuctionCard from '../BidAuctionCard/BidAuctionCard'
import useFilterAuctions from '../../utilities/hooks/useFilterAuctions'
import PaginationLayout from '@components/PaginationLayout'
import { useContext, useEffect, useRef } from 'react'
import { HistoryContext } from '../../contexts/HistoryContext'
import { delay } from '@components/BiddingButtons/functions'
import CategoryDescriptions from '@components/AuctionsListing/CategoryDescriptions'
import SkeletonCards from '@components/SkeletonCards'
import { useRouter } from 'next/router'

export default function AuctionsListing({
  title,
  previewDescription,
  baseUrl = '',
  props: {
    initialAuctions,
    categoryCMSData,
    auctionsCount,
    pageId,
    pageCount,
    sortedPickupDates,
    categories,
    locationGroupState,
  },
}: {
  title: string
  previewDescription: string
  props: any
  baseUrl: string
}) {
  const {
    auctions,
    pagination: { currentPage, totalPages, itemsPerPage, totalItems },
    handler: { changePage },
    isLoading,
  } = useFilterAuctions({
    initAuctions: initialAuctions,
    initPage: pageId,
    initTotalPages: pageCount,
    initTotalAuctions: auctionsCount,
  })
  const auctionsRef = useRef<any>({})

  const { path }: any = useContext(HistoryContext)

  const scrollToCard = async () => {
    await delay(100)
    const [pageName, auctionId] = path.prev.substring(1).split('/')
    if (pageName !== 'auction-detail') return
    let scrollToAuction = auctions.find(
      ({ id }: { id: number }) => id === Number(auctionId)
    )
    if (!scrollToAuction) return

    if (auctionsRef.current && auctionsRef.current[scrollToAuction.id]) {
      !auctionsRef.current[scrollToAuction.id].scrollIntoView({
        behavior: 'smooth',
        block: 'end',
        inline: 'nearest',
      })
    }
  }

  useEffect(() => {
    scrollToCard()
    return () => {
      auctionsRef.current = {}
    }
  }, [path])

  return (
    <MainLayout
      title={title}
      previewDescription={previewDescription}
      filterData={{
        locationGroupState,
        isStatePage: false,
        categories,
        pickupDates: sortedPickupDates,
      }}
      showFilter
    >
      <div
        className={`py-4 xl:pb-16 xl:flex min-h-112 relative overflow-y-auto`}
      >
        <div className="w-full ">
          {baseUrl.includes('/location') && (
            <div className="flex flex-col">
              <h2 className="font-bold text-26 pt-8">
                Viewing auctions in{' '}
                <span className="capitalize">
                  {(baseUrl.split('/').pop() || '').split('-').join(' ')}
                </span>
              </h2>
              <Link className="underline" href="/category/all/1">
                View all Auctions
              </Link>
            </div>
          )}
          <div>
            <CategoryDescriptions categoryCMSData={categoryCMSData} />
            <PaginationLayout
              current_page={currentPage}
              per_page={itemsPerPage}
              total={totalItems}
              total_pages={totalPages}
              pageChange={changePage}
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 2xl:grid-cols-4 gap-5 md:gap-6">
                {!isLoading ? (
                  auctions.length ? (
                    auctions.map((auction: any) => (
                      <div
                        key={auction.id || auction}
                        className={auction.id ? 'block' : 'flex'}
                        ref={(el) => (auctionsRef.current[auction.id] = el)}
                      >
                        {auction.id && (
                          <BidAuctionCard auction={auction} key={auction.id} />
                        )}
                      </div>
                    ))
                  ) : (
                    <NoAuctionsFound changePage={changePage} />
                  )
                ) : (
                  <SkeletonCards />
                )}
              </div>
            </PaginationLayout>
          </div>
        </div>
      </div>
    </MainLayout>
  )
}

const NoAuctionsFound = ({
  changePage,
}: {
  changePage: (pageNumber: number) => void
}) => {
  const router = useRouter()

  useEffect(() => {
    if (Number(router.query.pageId) === 1) return
    changePage(1)
  }, [])
  return (
    <p className="text-center col-span-3">
      Sorry no auctions available.
      <Link className="font-bold underline" href={'/category/all/1'}>
        View All Auctions
      </Link>
    </p>
  )
}
